/**
 * The `posture` and `emailSecurity` chart sections of the PDF report.
 *
 * Data comes from `getReport().telemetry`, which is the snapshot frozen into
 * generationParamsJson at generation time — so these charts always draw what the
 * report was approved with, not whatever telemetry exists today.
 *
 * Honesty rules these sections must keep, because the alternative is a chart
 * that lies to a customer's leadership team:
 *  - `gated` (tenant isn't licensed) never renders as zero.
 *  - `empty` (licensed, genuinely no events) never renders as a gap.
 *  - a `truncated` advanced-hunting window is stated, because it makes the
 *    figure a floor rather than a total.
 */

import {
  PdfBarList,
  PdfDonut,
  PdfStackedBar,
  PdfStatTile,
  PdfTrendLine,
  type BarDatum,
} from './PdfCharts';

export interface ReportTelemetryMetric {
  metricKey: string;
  label: string;
  status: 'ok' | 'empty' | 'gated' | 'error' | 'never_collected';
  queryVersion: number | null;
  stale: boolean;
  result: Record<string, unknown> | null;
}

/** The preceding calendar month, when ops-api found one to freeze alongside. */
export interface PriorPeriodTelemetry {
  periodStart: string;
  periodEnd: string;
  capturedAt: string | null;
  metrics: ReportTelemetryMetric[];
}

/** One month of Secure Score, accumulated one report at a time. */
export interface SecureScorePoint {
  period: string;
  percentage: number;
}

export interface ReportTelemetryHistory {
  secureScore: SecureScorePoint[];
}

export interface ReportTelemetry {
  periodStart: string;
  periodEnd: string;
  capturedAt: string | null;
  metrics: ReportTelemetryMetric[];
  hasData: boolean;
  previous?: PriorPeriodTelemetry | null;
  history?: ReportTelemetryHistory | null;
}

function metric(t: ReportTelemetry | null | undefined, key: string): ReportTelemetryMetric | null {
  return t?.metrics.find((m) => m.metricKey === key) ?? null;
}

const num = (r: Record<string, unknown> | null, k: string): number | null =>
  r && typeof r[k] === 'number' ? (r[k] as number) : null;

function priorResult(
  t: ReportTelemetry | null | undefined,
  key: string,
): Record<string, unknown> | null {
  const m = t?.previous?.metrics.find((x) => x.metricKey === key);
  return m?.status === 'ok' ? (m.result ?? null) : null;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** "2026-07-01" -> "July". Executives read month names, not date ranges. */
function monthLabel(isoDate: string | undefined): string | null {
  if (!isoDate || isoDate.length < 7) return null;
  const month = Number(isoDate.slice(5, 7));
  return month >= 1 && month <= 12 ? MONTHS[month - 1] : null;
}

/** 'Jul' from '2026-07'. */
function shortMonth(period: string): string {
  const m = Number(period.slice(5, 7));
  return m >= 1 && m <= 12 ? MONTHS[m - 1].slice(0, 3) : period;
}

/**
 * A period is partial when it was collected before it finished — which is what
 * happens whenever an analyst previews the month they are standing in. The
 * existing `truncated` flag does not cover this: it only reports the retention
 * clamp at the *start* of the window, so an in-progress month arrives looking
 * like a complete one.
 */
function isPartialPeriod(periodEnd: string, capturedAt: string | null): boolean {
  if (!capturedAt) return false;
  return capturedAt.slice(0, 10) <= periodEnd;
}

/**
 * Month-on-month movement, stated as an executive would say it.
 *
 * Percentages only. Raw Secure Score points are the trap here: Microsoft moved
 * AAT's achievable total from 1291 to 1324 mid-August, so the points went up 31
 * while the actual share of the score earned moved 0.4 — a "+31 points" line
 * would have been a fiction with a real number attached.
 */
function percentMoveNote(
  currentPct: number | null,
  priorPct: number | null,
  priorMonth: string | null,
): string | null {
  if (currentPct === null || priorPct === null || !priorMonth) return null;
  const now = Math.round(currentPct);
  const before = Math.round(priorPct);
  if (now === before) return `Level with ${priorMonth}.`;
  return `${now > before ? 'Up' : 'Down'} from ${before}% in ${priorMonth}.`;
}

/**
 * Whether two periods can have their *counts* compared at all.
 *
 * Counts scale with the length of the window they were measured over, and the
 * windows are routinely different lengths: advanced hunting keeps 30 days, so a
 * month collected late covers a fortnight of itself. Putting "up 87%" beside a
 * fortnight measured against a full month would be the single most damaging
 * thing this page could do, so counts compare only when both windows ran the
 * whole month. Proportions are unaffected and compare freely.
 */
function countsComparable(
  telemetry: ReportTelemetry | null,
  currentResult: Record<string, unknown> | null,
  prior: Record<string, unknown> | null,
): boolean {
  if (!telemetry?.previous || !currentResult || !prior) return false;
  if (currentResult.truncated === true || prior.truncated === true) return false;
  if (isPartialPeriod(telemetry.periodEnd, telemetry.capturedAt)) return false;
  if (isPartialPeriod(telemetry.previous.periodEnd, telemetry.previous.capturedAt)) return false;
  return true;
}

/** Movement in a raw count, once `countsComparable` has allowed it. */
function countMoveNote(
  current: number | null,
  prior: number | null,
  priorMonth: string | null,
): string | null {
  if (current === null || prior === null || !priorMonth) return null;
  if (prior === current) return `The same as ${priorMonth}.`;
  return `${current > prior ? 'Up' : 'Down'} from ${prior.toLocaleString()} in ${priorMonth}.`;
}

/**
 * Defender reports platforms as run-together tokens ("WindowsServer2019"). An
 * executive should not be reading a Microsoft enum.
 */
function platformLabel(raw: unknown): string {
  if (typeof raw !== 'string' || raw.trim() === '') return 'unidentified devices';
  if (raw === 'Windows11WVD') return 'Windows 11 virtual desktops';
  return raw
    .replace(/^WindowsServer/, 'Windows Server ')
    .replace(/^Windows(\d)/, 'Windows $1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * "— 8 of them Windows 10". The obvious question on seeing an unprotected count
 * is *which ones*, and the answer is usually one platform doing most of the work.
 */
function gapDetail(result: Record<string, unknown> | null, gap: number | null): string {
  if (!result || gap === null || gap <= 0) return '';
  const raw = result.gapByPlatform;
  if (!Array.isArray(raw) || raw.length === 0) return '';
  const top = [...(raw as Array<{ platform?: unknown; count?: unknown }>)]
    .filter((e) => typeof e?.count === 'number')
    .sort((a, b) => (b.count as number) - (a.count as number))[0];
  if (!top) return '';
  const count = top.count as number;
  const label = platformLabel(top.platform);
  if (count >= gap) return `, all of them ${label}`;
  if (count > 1) return ` — ${count} of them ${label}`;
  return '';
}

/** The one place unavailable metrics get their wording. Keeping it central
 * stops "no data" and "not licensed" drifting into the same sentence. */
function unavailableReason(m: ReportTelemetryMetric | null): string | null {
  if (!m) return 'Not collected for this period.';
  switch (m.status) {
    case 'ok':
      return m.result ? null : 'Collected, but the result could not be read.';
    case 'empty':
      return 'No activity recorded in this period.';
    case 'gated':
      return 'Not available under this tenant’s current licensing.';
    case 'error':
      return 'Collection did not complete for this period.';
    case 'never_collected':
      return 'Not collected for this period.';
    default:
      return 'Not collected for this period.';
  }
}

function Note({ children }: { children: React.ReactNode }) {
  return <p className="pdf-chart-note">{children}</p>;
}


// ---------------------------------------------------------------------------
// Auto-suppression
// ---------------------------------------------------------------------------

/**
 * Whether a chart section has anything worth a page.
 *
 * The rule: suppress only when *every* metric the section draws is unavailable.
 * If even one has data the section renders, and the unavailable ones state why
 * — a partial page is informative, a page of nothing but apologies is not.
 *
 * This matters most in the monthly report, which every customer receives. A
 * tenant without Defender for Office would otherwise get a full page reading
 * "Not available under this tenant's current licensing" in the deliverable they
 * pay for. Omitting the section is not dishonest — the ops Telemetry tab still
 * shows exactly which metrics were gated and why.
 */
function anyRenderable(t: ReportTelemetry | null, keys: string[]): boolean {
  return keys.some((k) => {
    const m = metric(t, k);
    return m?.status === 'ok' && m.result !== null;
  });
}

export const POSTURE_METRIC_KEYS = [
  'secure_score_trend',
  'mfa_registration_coverage',
  'secure_score_top_actions',
];

export const EMAIL_SECURITY_METRIC_KEYS = [
  'email_post_delivery_remediation',
  'email_threats_blocked',
  'url_click_protection',
];

export function hasPostureContent(t: ReportTelemetry | null): boolean {
  return anyRenderable(t, POSTURE_METRIC_KEYS);
}

export function hasEmailSecurityContent(t: ReportTelemetry | null): boolean {
  return anyRenderable(t, EMAIL_SECURITY_METRIC_KEYS);
}

// ---------------------------------------------------------------------------
// Posture — secure score trend + MFA registration coverage
// ---------------------------------------------------------------------------

export function PdfPostureSection({ telemetry }: { telemetry: ReportTelemetry | null }) {
  const score = metric(telemetry, 'secure_score_trend');
  const mfa = metric(telemetry, 'mfa_registration_coverage');

  const scoreReason = unavailableReason(score);
  const mfaReason = unavailableReason(mfa);

  const samples = Array.isArray(score?.result?.samples)
    ? (score!.result!.samples as Array<{ date: string; current: number; max?: number }>)
    : [];
  // Plotted as a percentage, not raw points: the headline reads "75%" and an
  // axis in the 1140s for the same metric reads as a different number entirely.
  // Percentage also absorbs the max moving as Microsoft adds controls.
  const points = samples
    .filter((s) => s && typeof s.current === 'number' && typeof s.date === 'string')
    .map((s) => ({
      date: s.date.slice(5),
      value: s.max ? Math.round((s.current / s.max) * 1000) / 10 : s.current,
    }));

  const latestPct = num(score?.result ?? null, 'latestPercentage');
  // Deliberately NOT `deltaPoints`. Microsoft moves the achievable total as it
  // adds controls — AAT's went 1291 -> 1324 mid-August, which reads as "+31
  // points" while the share of the score actually earned moved 0.4. The move
  // across the period is only honest as a percentage of what was achievable.
  const earliestPct = num(score?.result ?? null, 'earliestPercentage');
  const pctMove =
    latestPct !== null && earliestPct !== null
      ? Math.round((latestPct - earliestPct) * 10) / 10
      : null;

  const mfaPct = num(mfa?.result ?? null, 'mfaRegisteredPct');
  const adminPct = num(mfa?.result ?? null, 'adminsMfaRegisteredPct');
  const members = num(mfa?.result ?? null, 'totalMembers');

  return (
    <div className="pdf-section-body">
      <div className="pdf-chart-grid">
        <div className="pdf-chart-card">
          <p className="pdf-chart-card__title">Microsoft Secure Score</p>
          {scoreReason ? (
            <Note>{scoreReason}</Note>
          ) : (
            <>
              <PdfTrendLine points={points} widthMm={72} heightMm={34} valueSuffix="%" percent />
              <p className="pdf-chart-card__caption">
                {latestPct !== null && `Ended the period at ${latestPct}% of the achievable score`}
                {pctMove !== null && (
                  <>
                    {' '}
                    ({pctMove === 0
                      ? 'level across the month'
                      : `${pctMove > 0 ? 'up' : 'down'} ${Math.abs(pctMove)} percentage points across the month`}
                    ).
                  </>
                )}
              </p>
            </>
          )}
        </div>

        <div className="pdf-chart-card">
          <p className="pdf-chart-card__title">Multi-factor coverage</p>
          {mfaReason ? (
            <Note>{mfaReason}</Note>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '6mm', alignItems: 'center' }}>
                <PdfDonut percent={mfaPct ?? 0} label="Members" sizeMm={30} />
                <PdfDonut
                  percent={adminPct ?? 0}
                  label="Admins"
                  sizeMm={30}
                  stroke="var(--pdf-navy)"
                />
              </div>
              <p className="pdf-chart-card__caption">
                {mfaPct !== null && members !== null && (
                  <>
                    {mfaPct}% of {members.toLocaleString()} licensed members are registered for
                    multi-factor authentication
                  </>
                )}
                {adminPct !== null && (
                  <>
                    {' '}
                    {adminPct === 100
                      ? '— all administrator accounts are covered.'
                      : `— administrator coverage is ${adminPct}%, which is the more urgent gap.`}
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>

      <TopActions telemetry={telemetry} />
    </div>
  );
}

/**
 * The "what to do next" panel. Deliberately below the two charts: the trend
 * answers "how are we doing", this answers "what would move it".
 */
function TopActions({ telemetry }: { telemetry: ReportTelemetry | null }) {
  const m = metric(telemetry, 'secure_score_top_actions');
  const reason = unavailableReason(m);
  const actions = Array.isArray(m?.result?.actions)
    ? (m!.result!.actions as Array<{ title: string; gap: number; service: string | null }>)
    : [];
  const available = num(m?.result ?? null, 'totalPointsAvailable');

  if (reason || actions.length === 0) {
    return (
      <div className="pdf-chart-card" style={{ marginTop: '6mm' }}>
        <p className="pdf-chart-card__title">Highest-value improvements</p>
        <Note>{reason ?? 'No outstanding improvement actions were identified.'}</Note>
      </div>
    );
  }

  return (
    <div className="pdf-chart-card" style={{ marginTop: '6mm' }}>
      <p className="pdf-chart-card__title">Highest-value improvements</p>
      <PdfBarList
        // Control titles run long ("Block executable files from running unless
        // they meet a prevalence, age, or trusted list criterion"); SVG text
        // does not wrap or clip, so it must be truncated before it overlaps.
        data={actions.map((a) => ({
          label: a.title.length > 62 ? `${a.title.slice(0, 61)}…` : a.title,
          value: a.gap,
        }))}
        widthMm={160}
        labelWidthMm={104}
        rowHeightMm={6.4}
        valueSuffix=" pts"
      />
      {available !== null && (
        <p className="pdf-chart-card__caption">
          Completing these five would add {available} points to the Secure Score.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email security — post-delivery remediation
// ---------------------------------------------------------------------------

export function PdfEmailSecuritySection({ telemetry }: { telemetry: ReportTelemetry | null }) {
  const email = metric(telemetry, 'email_post_delivery_remediation');
  const reason = unavailableReason(email);

  const r = email?.result ?? null;
  const removed = num(r, 'totalRemediated');
  const totalActions = num(r, 'totalActions');
  const truncated = r?.truncated === true;
  const windowStart = typeof r?.windowStart === 'string' ? (r.windowStart as string) : null;
  const windowEnd = typeof r?.windowEnd === 'string' ? (r.windowEnd as string) : null;

  const breakdown = Array.isArray(r?.breakdown)
    ? (r!.breakdown as Array<{ action: string; count: number; removal?: boolean }>)
    : [];
  const bars: BarDatum[] = breakdown
    .filter((b) => b && typeof b.count === 'number')
    .slice(0, 6)
    .map((b) => ({
      label: b.action,
      value: b.count,
      // Non-removal actions (e.g. ZAP reprocessing) are shown but visually
      // subordinate, so the eye doesn't read them as remediation.
      color: b.removal === false ? '#b9bec9' : 'var(--pdf-blue)',
    }));

  return (
    <div className="pdf-section-body">
      {reason ? (
        <Note>{reason}</Note>
      ) : (
        <>
          <div className="pdf-stat-row">
            <PdfStatTile
              value={removed !== null ? removed.toLocaleString() : '—'}
              label="Removed after delivery"
              sub="Messages pulled back from inboxes"
            />
            <PdfStatTile
              value={totalActions !== null ? totalActions.toLocaleString() : '—'}
              label="Post-delivery actions"
              sub="Including re-evaluation"
              accent="var(--pdf-navy)"
            />
            <PdfStatTile
              value={windowStart && windowEnd ? `${windowStart.slice(5)}–${windowEnd.slice(5)}` : '—'}
              label="Window observed"
              sub={truncated ? 'Shortened by data retention' : 'Full reporting period'}
              accent="var(--pdf-navy)"
            />
          </div>

          {bars.length > 0 && (
            <div className="pdf-chart-card" style={{ marginTop: '6mm' }}>
              <p className="pdf-chart-card__title">Actions taken</p>
              <PdfBarList data={bars} widthMm={160} labelWidthMm={56} />
            </div>
          )}

          <ThreatsAndClicks telemetry={telemetry} />

          {truncated && (
            <Note>
              Microsoft retains this telemetry for 30 days, so the figures above cover{' '}
              {windowStart} to {windowEnd} rather than the full reporting period. Treat them as a
              minimum.
            </Note>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Pre-delivery blocking and Safe Links verdicts, paired: together with
 * post-delivery remediation they tell the whole story — what was stopped at the
 * door, what got through and was clawed back, and what users actually clicked.
 */
function ThreatsAndClicks({ telemetry }: { telemetry: ReportTelemetry | null }) {
  const threats = metric(telemetry, 'email_threats_blocked');
  const clicks = metric(telemetry, 'url_click_protection');
  const threatsReason = unavailableReason(threats);
  const clicksReason = unavailableReason(clicks);

  const breakdown = Array.isArray(threats?.result?.breakdown)
    ? (threats!.result!.breakdown as Array<{ threatType: string; blocked: number; delivered: number }>)
    : [];
  const bars: BarDatum[] = breakdown
    .slice(0, 4)
    .map((b) => ({ label: b.threatType, value: b.blocked }));
  const totalBlocked = num(threats?.result ?? null, 'totalBlocked');

  const linksScanned = num(clicks?.result ?? null, 'linksScanned');
  const threatsBlocked = num(clicks?.result ?? null, 'threatsBlocked');

  return (
    <div className="pdf-chart-grid">
      <div className="pdf-chart-card">
        <p className="pdf-chart-card__title">Threats blocked before delivery</p>
        {threatsReason || bars.length === 0 ? (
          <Note>{threatsReason ?? 'No threats were detected in inbound mail this period.'}</Note>
        ) : (
          <>
            <PdfBarList data={bars} widthMm={72} labelWidthMm={26} rowHeightMm={6} />
            {totalBlocked !== null && (
              <p className="pdf-chart-card__caption">
                {totalBlocked.toLocaleString()} malicious message
                {totalBlocked === 1 ? '' : 's'} stopped before reaching a mailbox.
              </p>
            )}
          </>
        )}
      </div>

      <div className="pdf-chart-card">
        <p className="pdf-chart-card__title">Safe Links click protection</p>
        {clicksReason ? (
          <Note>{clicksReason}</Note>
        ) : (
          <>
            {/*
              Deliberately two counts rather than a "percent blocked" donut.
              Every click is scanned, so on a healthy month blocked is zero —
              which a percentage renders as 0% protection, i.e. the opposite of
              the truth.
            */}
            <div className="pdf-stat-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
              <PdfStatTile
                value={(linksScanned ?? 0).toLocaleString()}
                label="Links scanned"
                sub="Checked at time of click"
              />
              <PdfStatTile
                value={(threatsBlocked ?? 0).toLocaleString()}
                label="Threats blocked"
                sub="Malicious destinations stopped"
                accent={threatsBlocked ? 'var(--pdf-blue)' : 'var(--pdf-navy)'}
              />
            </div>
            <p className="pdf-chart-card__caption">
              {linksScanned !== null && threatsBlocked !== null && (
                threatsBlocked === 0
                  ? `Every one of the ${linksScanned.toLocaleString()} links opened this period was checked at the point of click; none resolved to a malicious destination.`
                  : `${threatsBlocked.toLocaleString()} of ${linksScanned.toLocaleString()} links opened this period resolved to a malicious destination and were blocked.`
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Executive digest — the whole report on one page
// ---------------------------------------------------------------------------

export interface ExecDigestCopy {
  /** The authored executive narrative. Leads the page. */
  summary?: string;
  /**
   * Method note. Lives here rather than on the cover: a tenant with escalations
   * fills the cover (Windermere's ran a three-line dek plus three named
   * incidents) and this was the first thing to collide with the footer. It also
   * reads better at the end of the document than the front.
   */
  note?: string;
}

/**
 * The executive summary product is cover → this page → closing. Nothing else.
 *
 * Deliberately not a smaller version of the posture and email pages: an
 * executive reader wants the narrative first and the numbers as evidence, not a
 * dashboard. So it leads with the authored summary, then four headline figures,
 * then the single "what would move it" panel.
 *
 * Incident volume is intentionally absent — the cover already carries
 * detections / resolved / merged, and repeating it on page two wastes the only
 * page this product has.
 */
export function PdfExecDigestSection({ telemetry }: { telemetry: ReportTelemetry | null }) {
  const score = metric(telemetry, 'secure_score_trend');
  const mfa = metric(telemetry, 'mfa_registration_coverage');
  const threats = metric(telemetry, 'email_threats_blocked');
  const postDelivery = metric(telemetry, 'email_post_delivery_remediation');
  const clicks = metric(telemetry, 'url_click_protection');
  const devices = metric(telemetry, 'device_protection_coverage');
  const signIns = metric(telemetry, 'blocked_signin_attempts');

  const scorePct = num(score?.result ?? null, 'latestPercentage');
  const latest = (score?.result?.latest ?? null) as { current?: number; max?: number } | null;

  const mfaPct = num(mfa?.result ?? null, 'mfaRegisteredPct');
  const mfaRegistered = num(mfa?.result ?? null, 'mfaRegistered');
  const members = num(mfa?.result ?? null, 'totalMembers');
  const adminPct = num(mfa?.result ?? null, 'adminsMfaRegisteredPct');

  const blocked = num(threats?.result ?? null, 'totalBlocked');
  const delivered = num(threats?.result ?? null, 'totalDelivered');
  const removed = num(postDelivery?.result ?? null, 'totalRemediated');

  const scanned = num(clicks?.result ?? null, 'linksScanned');
  const dangerous = num(clicks?.result ?? null, 'threatsBlocked');

  const devicePct = num(devices?.result ?? null, 'protectedPct');
  const deviceProtected = num(devices?.result ?? null, 'protectedDevices');
  const deviceEligible = num(devices?.result ?? null, 'eligibleDevices');
  const deviceGap = num(devices?.result ?? null, 'unprotectedDevices');

  const blockedSignIns = num(signIns?.result ?? null, 'blockedAttempts');
  const accountsTargeted = num(signIns?.result ?? null, 'accountsTargeted');
  const accountsCapped = signIns?.result?.accountsTargetedCapped === true;

  // Month-on-month. Everything below stays silent when there is no prior
  // snapshot, which is the normal state until October's report — nothing was
  // collected before August 2026.
  const priorMonth = monthLabel(telemetry?.previous?.periodStart);
  const priorScore = priorResult(telemetry, 'secure_score_trend');
  const priorMfa = priorResult(telemetry, 'mfa_registration_coverage');
  const priorDevices = priorResult(telemetry, 'device_protection_coverage');
  const priorThreats = priorResult(telemetry, 'email_threats_blocked');
  const priorSignIns = priorResult(telemetry, 'blocked_signin_attempts');

  const scoreMove = percentMoveNote(scorePct, num(priorScore, 'latestPercentage'), priorMonth);
  const mfaMove = percentMoveNote(mfaPct, num(priorMfa, 'mfaRegisteredPct'), priorMonth);
  const deviceMove = percentMoveNote(devicePct, num(priorDevices, 'protectedPct'), priorMonth);
  const emailMove = countsComparable(telemetry, threats?.result ?? null, priorThreats)
    ? countMoveNote(blocked, num(priorThreats, 'totalBlocked'), priorMonth)
    : null;
  const signInMove = countsComparable(telemetry, signIns?.result ?? null, priorSignIns)
    ? countMoveNote(blockedSignIns, num(priorSignIns, 'blockedAttempts'), priorMonth)
    : null;

  /**
   * The period was still running when these figures were taken — every count on
   * the page is therefore a part-month. Saying so is the difference between a
   * floor and a total, exactly as with a truncated hunting window, and the
   * `truncated` flag does not cover this case.
   */
  const partial =
    telemetry && isPartialPeriod(telemetry.periodEnd, telemetry.capturedAt)
      ? `Covers ${Number(telemetry.periodStart.slice(8, 10))}\u2013${Number(
          telemetry.capturedAt!.slice(8, 10),
        )} ${monthLabel(telemetry.periodStart) ?? ''} only — the month was still running, so every count here is a part-month figure.`
      : null;

  /**
   * Both advanced hunting and sign-in logs retain 30 days, so a period
   * collected late covers only part of it. Saying so is the difference between
   * a floor and a total — an executive has no other way to know the window was
   * short.
   */
  const window = (m: ReportTelemetryMetric | null): string | null => {
    const r = m?.result;
    if (!r || r.truncated !== true) return null;
    const from = typeof r.windowStart === 'string' ? r.windowStart.slice(5) : null;
    const to = typeof r.windowEnd === 'string' ? r.windowEnd.slice(5) : null;
    return from && to ? `Measured ${from} to ${to} (Microsoft keeps 30 days).` : null;
  };

  const samples = Array.isArray(score?.result?.samples)
    ? (score!.result!.samples as Array<{ date: string; current: number; max?: number }>)
    : [];
  const dailyPoints = samples
    .filter((s) => s && typeof s.current === 'number' && typeof s.date === 'string')
    .map((s) => ({
      date: s.date.slice(5),
      value: s.max ? Math.round((s.current / s.max) * 1000) / 10 : s.current,
    }));

  /**
   * Secure Score is the one figure here that is worth anything as a long trend,
   * and the one we cannot go and fetch: Microsoft keeps about 90 days and
   * nothing before it. So the chart plots the months we have accumulated rather
   * than the days of this one, and the caption says how far back that actually
   * reaches — otherwise a two-month line reads as a two-year one.
   *
   * Below two months there is no trend to draw, so it falls back to the daily
   * samples within the period and says the history is still building.
   */
  const monthly = telemetry?.history?.secureScore ?? [];
  const useMonthly = monthly.length >= 2;
  const points = useMonthly
    ? monthly.map((m) => ({ date: shortMonth(m.period), value: m.percentage }))
    : dailyPoints;
  const historyNote = useMonthly
    ? `Monthly since ${shortMonth(monthly[0].period)} ${monthly[0].period.slice(0, 4)}.`
    : 'Microsoft keeps 90 days; grows each month.';

  return (
    <div className="pdf-section-body">
      {/*
        Bands of graphics, not a grid of numbers.
        A figure in a box is not a visualisation — the page previously carried
        five large numbers and one chart, which is why it read as empty however
        large the type got. Each band now shows a shape: a trend, a proportion,
        a flow. Nothing here needs explaining, which is the brief.
      */}
      {scorePct !== null && (
        <div className="pdf-score-hero">
          <div>
            <p className="pdf-chart-card__title">Microsoft Secure Score</p>
            <div className="pdf-score-hero__value">{Math.round(scorePct)}%</div>
            <p className="pdf-score-hero__sub">
              {latest?.current && latest?.max
                ? `${Math.round(latest.current)} of ${Math.round(latest.max)} points`
                : 'of the score available to you'}
            </p>
            {scoreMove && <p className="pdf-score-hero__move">{scoreMove}</p>}
            {/* Under the figure rather than under the chart. The left column runs
                shorter than the 36mm trend line, so a line here is free, whereas
                one below the chart grows the hero and pushes the page's last
                paragraph through the footer rule. */}
            <p className="pdf-score-hero__history">{historyNote}</p>
          </div>
          {points.length > 0 && (
            <PdfTrendLine points={points} widthMm={96} heightMm={36} valueSuffix="%" percent />
          )}
        </div>
      )}

      <div className="pdf-band-grid">
        {mfaPct !== null && (
          <div className="pdf-band">
            <p className="pdf-chart-card__title">Two-step sign-in</p>
            <div className="pdf-band__body pdf-band__body--donut">
              <PdfDonut percent={mfaPct} label="Staff" sizeMm={30} />
              <div className="pdf-band__facts">
                <p>
                  <strong>
                    {mfaRegistered !== null && members !== null
                      ? `${mfaRegistered.toLocaleString()} of ${members.toLocaleString()}`
                      : `${Math.round(mfaPct)}%`}
                  </strong>{' '}
                  staff accounts are protected by a second step at sign-in.
                </p>
                {adminPct !== null && (
                  <p className="pdf-band__note">
                    {adminPct === 100
                      ? 'Every administrator account is covered.'
                      : `${Math.round(adminPct)}% of administrator accounts are covered.`}
                  </p>
                )}
                {mfaMove && <p className="pdf-band__note">{mfaMove}</p>}
              </div>
            </div>
          </div>
        )}

        {devicePct !== null && (
          <div className="pdf-band">
            {/*
              Not "Computers protected". That title says nothing about who is
              protecting what, and an executive reads the shortfall as their
              security provider leaving 9% of the estate uncovered. The gap is
              actually machines their own IT has not enrolled yet — real, worth
              telling them, and not a monitoring failure. So the band says what
              the monitoring is, and locates the gap where it belongs.
            */}
            <p className="pdf-chart-card__title">Computers we're monitoring</p>
            <div className="pdf-band__body pdf-band__body--donut">
              <PdfDonut percent={devicePct} label="Monitored" sizeMm={30} stroke="var(--pdf-navy)" />
              <div className="pdf-band__facts">
                <p>
                  <strong>
                    {deviceProtected !== null && deviceEligible !== null
                      ? `${deviceProtected.toLocaleString()} of ${deviceEligible.toLocaleString()}`
                      : `${Math.round(devicePct)}%`}
                  </strong>{' '}
                  company computers are watched by our analysts around the clock.
                </p>
                {deviceGap !== null && deviceGap > 0 && (
                  <p className="pdf-band__note">
                    {/*
                      Three things an executive asks in order: why aren't those in
                      the number above, which ones are they, and what fixes it.
                      "Not enrolled" answered none of them — the word only means
                      something to whoever administers Defender. The cause is that
                      the sensor was never installed on those machines, which is
                      both the honest answer and the action, so it says that.
                    */}
                    The other {deviceGap}{' '}
                    {deviceGap === 1 ? "doesn't" : "don't"} have the security software
                    installed yet{gapDetail(devices?.result ?? null, deviceGap)}.
                  </p>
                )}
                {deviceMove && <p className="pdf-band__note">{deviceMove}</p>}
                {window(devices) && <p className="pdf-band__note">{window(devices)}</p>}
              </div>
            </div>
          </div>
        )}

        {blocked !== null && (
          <div className="pdf-band">
            <p className="pdf-chart-card__title">What happened to your email</p>
            <div className="pdf-band__body pdf-band__body--stack">
              {/* Blocked / delivered / clawed back is a picture of the service
                  working; "1,162 blocked" on its own is just a number. */}
              <PdfStackedBar
                segments={[
                  { label: 'Blocked', value: blocked, color: 'var(--pdf-blue)' },
                  ...(removed !== null && removed > 0
                    ? [{ label: 'Removed after arrival', value: removed, color: 'var(--pdf-navy)' }]
                    : []),
                  ...(delivered !== null && delivered > 0
                    ? [{ label: 'Delivered', value: delivered, color: '#c9cdd8' }]
                    : []),
                ]}
                widthMm={78}
                heightMm={7}
              />
              <p className="pdf-band__facts">
                <strong>{blocked.toLocaleString()}</strong> malicious messages were stopped
                before anyone saw them
                {removed !== null && removed > 0 && (
                  <>
                    , and a further <strong>{removed.toLocaleString()}</strong> were pulled back
                    out of inboxes afterwards
                  </>
                )}
                .
              </p>
              {scanned !== null && (
                <p className="pdf-band__note">
                  {scanned.toLocaleString()} links were checked at the moment staff clicked them
                  {dangerous && dangerous > 0
                    ? `; ${dangerous.toLocaleString()} led somewhere dangerous and were blocked.`
                    : '; none led anywhere dangerous.'}
                </p>
              )}
              {emailMove && <p className="pdf-band__note">{emailMove}</p>}
              {/* Every figure in this band comes off the same hunting window, so
                  one note covers all three. Windermere's July report otherwise
                  presented a 14-day count as the month's total. */}
              {(window(threats) ?? window(postDelivery) ?? window(clicks)) && (
                <p className="pdf-band__note">
                  {window(threats) ?? window(postDelivery) ?? window(clicks)}
                </p>
              )}
            </div>
          </div>
        )}
        {blockedSignIns !== null && (
          <div className="pdf-band">
            <p className="pdf-chart-card__title">Attempts to break into accounts</p>
            <div className="pdf-band__body pdf-band__body--stack">
              <div className="pdf-band__big">{blockedSignIns.toLocaleString()}</div>
              <p className="pdf-band__facts">
                attempts to sign in with the wrong password were stopped. Every one of them
                failed.
              </p>
              {/* "Was this one forgetful person or someone working through our
                  staff list?" is the question the count provokes, and it is the
                  one an executive actually cares about. */}
              {accountsTargeted !== null && accountsTargeted > 0 && (
                <p className="pdf-band__note">
                  {accountsTargeted === 1
                    ? 'All of them against a single account.'
                    : `Spread across ${accountsCapped ? 'at least ' : ''}${accountsTargeted.toLocaleString()} different staff accounts.`}
                </p>
              )}
              {signInMove && <p className="pdf-band__note">{signInMove}</p>}
              {window(signIns) && <p className="pdf-band__note">{window(signIns)}</p>}
            </div>
          </div>
        )}
      </div>

      {partial && <p className="pdf-chart-note pdf-digest-footnote">{partial}</p>}
    </div>
  );
}

/** The digest always has the authored narrative, so it is never suppressed —
 *  but it is worth knowing whether any figure will be real. */
export function hasExecDigestTelemetry(t: ReportTelemetry | null): boolean {
  return anyRenderable(t, [...POSTURE_METRIC_KEYS, ...EMAIL_SECURITY_METRIC_KEYS]);
}
